import { createAsyncThunk } from '@reduxjs/toolkit';
import { client } from '@shared/api/client';
import { withProgramsGrantWhereModuleOn } from '@entities/user';

export const checkIsLogin = () => {
  return localStorage.getItem('isLogin');
};

export const setIsLoginToStorage = val => {
  localStorage.setItem('isLogin', val);
};

export const login = createAsyncThunk('user/login', (...args) =>
  client.post('/auth/tokens', ...args)
);

export const logout = createAsyncThunk('user/logout', (...args) =>
  client.delete('/auth/tokens', ...args)
);

// Idle / dialog logout (features/session-idle): the API call comes first
// so the server-side session is invalidated, and the outcome never
// matters — a 410 means the session was already over, a network failure
// leaves nothing this client could still use. The user slice clears the
// signed-in state on `fulfilled` like it does for `logout`.
export const endSession = createAsyncThunk('user/endSession', async (_, { dispatch }) => {
  try {
    // keepalive: the idle logout navigates away right after this, and a
    // tab the browser froze may be unfrozen only for a moment; the
    // request must survive both.
    await client.delete('/auth/tokens', undefined, { dispatch, silent410: true, keepalive: true });
  } catch (_) {
    // see above
  }
  return { ok: true };
});

export const getUser = createAsyncThunk('user/getUser', (...args) => client.get('/user', ...args));

export const getMetadata = createAsyncThunk('user/getMetadata', (...args) =>
  client.get('/auth/token/metadata', ...args)
);

export const updateUser = createAsyncThunk('user/updateUser', (...args) =>
  client.post('/user', ...args)
);

export const removeUserFromAllAccounts = createAsyncThunk(
  'userAccountManagement/removeUserFromAllAccounts',
  async ({ accountIds, userUuid }, { rejectWithValue, getState }) => {
    try {
      if (!accountIds || !Array.isArray(accountIds) || accountIds.length === 0) {
        return rejectWithValue({
          error: 'No valid account IDs provided',
          status: 400,
        });
      }

      const requestBody = {
        accountsToAdd: [],
        accountsToRemove: accountIds,
        permissions: {
          managesUsers: false,
          administerClaims: false,
          receiveClaimsEmails: false,
          receiveSurveysEmails: false,
          viewReports: false,
        },
      };

      // Call new API endpoint
      const response = await client.post(`/users/${userUuid}/accounts`, requestBody);

      if (response.ok) {
        return {
          success: true,
          accountIds,
          removedAccountsCount: accountIds.length,
          message:
            response.data?.message ||
            `User removed from ${accountIds.length} account(s) successfully`,
        };
      } else {
        // Handle validation errors from the API
        if (response.data?.errors && Array.isArray(response.data.errors)) {
          const validationErrors = {};
          response.data.errors.forEach(error => {
            validationErrors[error.field] = error.message;
          });

          return rejectWithValue({
            error: 'Validation failed',
            status: response.status,
            validationErrors,
          });
        }

        return rejectWithValue({
          error: response.data?.message || 'Failed to remove user from accounts',
          status: response.status,
        });
      }
    } catch (error) {
      return rejectWithValue({
        error: error.message || 'Network error occurred',
        status: 500,
      });
    }
  }
);

export const removeUserAccountAccess = createAsyncThunk(
  'userAccountManagement/removeUserAccountAccess',
  async ({ accountId, accountUuid, userUuid }, { rejectWithValue, getState }) => {
    try {
      // Prepare request body with single account ID in array format
      // Use the accountUuid passed from the component, or fallback to accountId
      const actualAccountId = accountUuid || accountId;

      if (!actualAccountId) {
        return rejectWithValue({
          error: 'No valid account ID provided',
          status: 400,
          accountId,
        });
      }

      const requestBody = {
        accountsToAdd: [],
        accountsToRemove: [actualAccountId],
        permissions: {
          managesUsers: false,
          administerClaims: false,
          receiveClaimsEmails: false,
          receiveSurveysEmails: false,
          viewReports: false,
        },
      };

      // Call new API endpoint
      const response = await client.post(`/users/${userUuid}/accounts`, requestBody);

      if (response.ok) {
        const state = getState();
        const { userAccounts } = state.userAccountManagement;
        const accountToRemove = userAccounts.find(acc => acc.id === accountId);

        return {
          success: true,
          accountId,
          accountName: accountToRemove?.name || 'account',
          message: response.data?.message || 'Account removed successfully',
        };
      } else {
        // Handle validation errors from the API
        if (response.data?.errors && Array.isArray(response.data.errors)) {
          // Convert array of errors to object format for easier access in components
          const validationErrors = {};
          response.data.errors.forEach(error => {
            validationErrors[error.field] = error.message;
          });

          return rejectWithValue({
            error: 'Validation failed',
            status: response.status,
            validationErrors: validationErrors,
            accountId,
          });
        }

        // Return error with proper structure
        return rejectWithValue({
          error: response.data?.message || 'Failed to remove account',
          status: response.status,
          accountId,
        });
      }
    } catch (error) {
      return rejectWithValue({
        error: error.message || 'Failed to remove account',
        status: error.status || 500,
        accountId,
      });
    }
  }
);

export const fetchUserWithAccounts = createAsyncThunk(
  'userAccountManagement/fetchUserWithAccounts',
  async ({ userUuid, accountUuid }, { rejectWithValue }) => {
    try {
      // Call new endpoint to get user with all accounts
      const response = await client.get(`/accounts/${accountUuid}/users/${userUuid}/with-accounts`);
      const userAccounts = response.data.data.userAccounts || [];
      const user = response.data.data;
      delete user.userAccounts;

      if (response.ok) {
        return {
          success: true,
          user: user,
          userAccounts: userAccounts,
        };
      } else {
        // Return error with proper structure
        return rejectWithValue({
          error: response.data?.message || 'API call failed',
          status: response.status,
        });
      }
    } catch (error) {
      return rejectWithValue({
        error: error.message || 'Failed to fetch user with accounts',
        status: error.status || 500,
      });
    }
  }
);

export const checkEmailExists = createAsyncThunk(
  'userAccountManagement/checkEmailExists',
  async ({ email }, { rejectWithValue }) => {
    try {
      const response = await client.get(`/users/lookup-by-email/${encodeURIComponent(email)}`, {
        method: 'GET',
      });

      // Handle 404 immediately if response indicates not found
      if (response.status === 404 || (!response.ok && response.status === 404)) {
        return {
          exists: false,
          user: null,
          email: email,
        };
      }

      if (response.ok && response.status === 200) {
        // User exists - got user data from response.data.data
        const userData = response.data.data;
        const {
          uuid,
          email,
          firstName,
          lastName,
          role,
          userPermissions,
          providerTitle,
          providerNPINumber,
          source,
        } = userData;

        const existingUser = {
          uuid: uuid, // Use userId if provided, fallback to email
          firstName: firstName,
          lastName: lastName,
          email: email,
          role: role, // Include role to determine if user exists in current account
          userPermissions: userPermissions, // Include permissions
          providerTitle: providerTitle, // Include provider info
          providerNPINumber: providerNPINumber, // Include provider info
          hasUserAcceptedInvitation: true, // Assume existing user has accepted invitation
          source: source, // Add source to determine if user is in current account or system
        };

        return {
          exists: true,
          user: existingUser,
          email: email,
        };
      } else if (response.status === 404) {
        return {
          exists: false,
          user: null,
          email: email,
        };
      } else {
        return rejectWithValue({
          error: response.data?.message || 'Failed to check email',
          status: response.status,
          email: email,
        });
      }
    } catch (error) {
      const errorString = typeof error === 'string' ? error : error.toString();

      // Check for 404 in multiple possible locations
      const status =
        error.status ||
        error.response?.status ||
        error.response?.data?.status ||
        (error.message?.includes('404') ? 404 : null);

      const isNotFound =
        status === 404 ||
        error.message?.includes('404') ||
        error.response?.status === 404 ||
        error.name === 'NotFoundError' ||
        errorString.includes('404') ||
        errorString.includes('Not Found') ||
        errorString.includes('the server responded with a status of 404') ||
        (error.toString && error.toString().includes('404'));

      if (isNotFound) {
        return {
          exists: false,
          user: null,
          email: email,
        };
      }

      return rejectWithValue({
        error: error.message || errorString || 'Error checking email',
        status: status,
        payload: error.payload,
        email: email,
      });
    }
  }
);

export const addAccountsToUser = createAsyncThunk(
  'userAccountManagement/addAccountsToUser',
  async ({ userUuid, accountIds, settings }, { rejectWithValue }) => {
    try {
      // Prepare request body according to API spec
      const requestBody = {
        accountsToAdd: accountIds,
        accountsToRemove: [],
        permissions: settings,
      };

      const response = await client.post(`/users/${userUuid}/accounts`, requestBody);

      if (response.ok) {
        return {
          success: true,
          accountIds,
          settings,
          message: response.data?.message || 'Accounts added successfully',
        };
      } else {
        // Handle validation errors from the API
        if (response.data?.validationErrors) {
          return rejectWithValue({
            error: 'Validation failed',
            status: response.status,
            validationErrors: response.data.validationErrors,
          });
        }

        // Return error with proper structure
        return rejectWithValue({
          error: response.data?.message || 'Failed to add accounts',
          status: response.status,
          validationErrors: null,
        });
      }
    } catch (error) {
      // Network or other errors
      return rejectWithValue({
        error: error.message || 'Failed to add accounts',
        status: error.status || 500,
        validationErrors: null,
      });
    }
  }
);

/**
 * Quiet re-read of the user's account links after accounts were added
 * optimistically: brings the fields only the server knows (whether
 * the account has the Programs module on, which grants it kept).
 * Unlike `fetchUserWithAccounts` it neither resets the selection nor
 * flips the page into its loading state; the slice merges the rows.
 */
export const refreshUserAccounts = createAsyncThunk(
  'userAccountManagement/refreshUserAccounts',
  async ({ userUuid, accountUuid }, { rejectWithValue }) => {
    try {
      const response = await client.get(`/accounts/${accountUuid}/users/${userUuid}/with-accounts`);
      if (response.ok) {
        return { userAccounts: response.data?.data?.userAccounts || [] };
      }
      return rejectWithValue({ error: response.data?.message || 'API call failed' });
    } catch (error) {
      return rejectWithValue({ error: error.message || 'Failed to refresh user accounts' });
    }
  }
);

export const validateUserData = createAsyncThunk(
  'userAccountManagement/validateUserData',
  async ({ formValues }, { rejectWithValue }) => {
    try {
      const response = await client.post('/users/validate', formValues);

      if (response.ok) {
        return {
          success: true,
          message: 'Validation successful',
        };
      } else {
        // Handle validation errors from the API
        if (response.data?.validationErrors) {
          return rejectWithValue({
            error: 'Validation failed',
            status: response.status,
            validationErrors: response.data.validationErrors,
          });
        }

        // Fallback: Handle array format errors (for backward compatibility)
        if (response.data?.errors && Array.isArray(response.data.errors)) {
          const validationErrors = {};
          response.data.errors.forEach(error => {
            validationErrors[error.field] = error.message;
          });

          return rejectWithValue({
            error: 'Validation failed',
            status: response.status,
            validationErrors: validationErrors,
          });
        }

        // Return error with proper structure
        return rejectWithValue({
          error: response.data?.message || 'Validation failed',
          status: response.status,
          validationErrors: null,
        });
      }
    } catch (error) {
      // Handle network errors or other exceptions
      return rejectWithValue({
        error: error.message || 'Failed to validate user data',
        status: error.status || 500,
        validationErrors: null,
      });
    }
  }
);

export const createUser = createAsyncThunk(
  'userAccountManagement/createUser',
  async ({ formValues }, { rejectWithValue, getState }) => {
    try {
      // Get current account from state
      const state = getState();
      const { currentAccount } = state.user.metadata;

      // Always use the new endpoint since account selection is mandatory
      const endpoint = `/accounts/${currentAccount.uuid}/users/create-with-accounts`;

      const response = await client.post(endpoint, formValues);

      if (response.ok) {
        // Handle successful response - data is in response.data.data
        const userData = response.data.data || response.data;

        // Create user object with the received data
        const createdUser = {
          uuid: userData.uuid || userData.userId,
          firstName: userData.firstName || formValues.firstName,
          lastName: userData.lastName || formValues.lastName,
          email: userData.email || formValues.email,
          phone: userData.phone || formValues.phone,
          createdAt: new Date().toISOString(),
        };

        return {
          success: true,
          createdUser: createdUser,
          userName: `${formValues.firstName || ''} ${formValues.lastName || ''}`.trim(),
          message: response.data?.message || 'User created successfully',
          createdWithAccounts: true,
        };
      } else {
        // Handle validation errors from the API
        if (response.data?.validationErrors) {
          return rejectWithValue({
            error: 'Validation failed',
            status: response.status,
            validationErrors: response.data.validationErrors,
          });
        }

        // Fallback: Handle array format errors (for backward compatibility)
        if (response.data?.errors && Array.isArray(response.data.errors)) {
          const validationErrors = {};
          response.data.errors.forEach(error => {
            validationErrors[error.field] = error.message;
          });

          return rejectWithValue({
            error: 'Validation failed',
            status: response.status,
            validationErrors: validationErrors,
          });
        }

        // Return error with proper structure
        return rejectWithValue({
          error: response.data?.message || 'Failed to create user',
          status: response.status,
          validationErrors: null,
        });
      }
    } catch (error) {
      // Handle network errors or other exceptions
      return rejectWithValue({
        error: error.message || 'Failed to create user',
        status: error.status || 500,
        validationErrors: null,
      });
    }
  }
);

export const editUserData = createAsyncThunk(
  'userAccountManagement/editUserData',
  async ({ currentAccountUuid, userUuid, formValues }, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      const { originalManagedUser } = state.userAccountManagement;

      if (!originalManagedUser) {
        return rejectWithValue({
          error: 'Original user data not available',
          status: 400,
        });
      }

      // Create patches by comparing only form fields (not system fields like uuid)
      const { compare } = await import('fast-json-patch');

      // Extract only form fields from originalManagedUser for comparison
      const originalFormData = {};
      Object.keys(formValues).forEach(key => {
        if (originalManagedUser.hasOwnProperty(key)) {
          originalFormData[key] = originalManagedUser[key];
        }
      });

      const patches = compare(originalFormData, formValues);

      if (patches.length === 0) {
        // No changes to save
        return {
          success: true,
          noChanges: true,
          userName: `${formValues.firstName || ''} ${formValues.lastName || ''}`.trim(),
        };
      }

      // Call PATCH API to update user data

      const response = await client.patch(`/users/${userUuid}`, patches);

      if (response.ok) {
        return {
          success: true,
          updatedUser: response.data,
          formValues, // Include formValues for state update
          userName: `${formValues.firstName || ''} ${formValues.lastName || ''}`.trim(),
        };
      } else {
        // Return error with proper structure
        return rejectWithValue({
          error: response.data?.message || 'API call failed',
          status: response.status,
          validationErrors: response.data?.validationErrors || null,
        });
      }
    } catch (error) {
      // Network or other errors
      return rejectWithValue({
        error: error.message || 'Failed to edit user',
        status: error.status || 500,
        validationErrors: null,
      });
    }
  }
);

export const saveUserAccountChanges = createAsyncThunk(
  'userAccountManagement/saveUserAccountChanges',
  async (
    { selectedAccountUuid, userUuid, formValues, callback },
    { rejectWithValue, getState }
  ) => {
    try {
      const state = getState();
      const { userAccounts, selectedAccount } = state.userAccountManagement;

      // Create patches by comparing only form fields (not system fields like uuid)
      const { compare } = await import('fast-json-patch');

      // The account being saved is the one in the request, not the one
      // selected in the sidebar: the bulk save fans this thunk out over
      // several accounts while the selection stays put.
      const targetAccountUuid = selectedAccountUuid || selectedAccount;
      const currentAccount = userAccounts.find(
        acc => (acc.accountUUID || acc.uuid || acc.id) === targetAccountUuid
      );

      if (!currentAccount) {
        return rejectWithValue({
          error: 'Current account not found',
          status: 400,
        });
      }

      const valuesToSave = withProgramsGrantWhereModuleOn(formValues, currentAccount);

      // Extract only form fields from current account for comparison
      const originalFormData = {};
      Object.keys(valuesToSave).forEach(key => {
        if (currentAccount.hasOwnProperty(key)) {
          originalFormData[key] = currentAccount[key];
        }
      });

      const patches = compare(originalFormData, valuesToSave);

      if (patches.length === 0) {
        // No changes to save
        return {
          success: true,
          noChanges: true,
          accountName: currentAccount.accountBusinessName || currentAccount.name || 'account',
        };
      }

      // Call PATCH API with new endpoint
      const response = await client.patch(
        `/users/${userUuid}/account-access/${targetAccountUuid}`,
        patches
      );

      if (response.ok) {
        callback && callback();

        return {
          success: true,
          updatedUser: response.data,
          formValues: valuesToSave, // Include formValues for state update
          accountName: currentAccount.accountBusinessName || currentAccount.name || 'account',
          selectedAccount: targetAccountUuid,
          // Lets the UI say that permission changes wait for the next sign-in.
          permissionsChanged: patches.some(p => p.path.startsWith('/userPermissions/')),
        };
      } else {
        // Handle validation errors from the API
        if (response.data?.validationErrors) {
          return rejectWithValue({
            error: 'Validation failed',
            status: response.status,
            validationErrors: response.data.validationErrors,
          });
        }

        // Return error with proper structure
        return rejectWithValue({
          error: response.data?.message || 'API call failed',
          status: response.status,
          validationErrors: null,
        });
      }
    } catch (error) {
      // Network or other errors
      return rejectWithValue({
        error: error.message || 'Failed to save changes',
        status: error.status || 500,
        validationErrors: null,
      });
    }
  }
);

/**
 * Bulk-save: fan out N parallel `saveUserAccountChanges` calls (one
 * per selected account) using `Promise.allSettled`, return the
 * partition of successes + failures so the UI can surface a partial
 * result snackbar.
 *
 * `settings` is a single template (role + provider + permissions) that
 * gets applied uniformly to every accountUuid. The per-call thunk
 * computes its own JSON Patch against each account's current values
 * (so unchanged fields aren't sent), so we don't need to dedupe here.
 */
export const saveUserAccountChangesBulk = createAsyncThunk(
  'userAccountManagement/saveUserAccountChangesBulk',
  async ({ userUuid, accountUuids, settings }, { dispatch, getState, rejectWithValue }) => {
    if (!Array.isArray(accountUuids) || accountUuids.length === 0) {
      return rejectWithValue({ error: 'No accounts selected', status: 400 });
    }
    const state = getState();
    const userAccounts = state.userAccountManagement.userAccounts || [];
    const nameOf = uuid => {
      const a = userAccounts.find(x => (x.accountUUID || x.uuid || x.id) === uuid);
      return a?.accountBusinessName || a?.businessName || a?.name || uuid;
    };

    const results = await Promise.allSettled(
      accountUuids.map(uuid =>
        dispatch(
          saveUserAccountChanges({
            selectedAccountUuid: uuid,
            userUuid,
            formValues: settings,
          })
        )
      )
    );

    const succeeded = [];
    const failed = [];
    results.forEach((r, idx) => {
      const accountUuid = accountUuids[idx];
      const accountName = nameOf(accountUuid);
      if (r.status === 'fulfilled' && saveUserAccountChanges.fulfilled.match(r.value)) {
        succeeded.push({ accountUuid, accountName });
      } else {
        const errPayload =
          r.status === 'fulfilled' ? r.value.payload : { error: r.reason?.message };
        failed.push({
          accountUuid,
          accountName,
          error: errPayload?.error || 'Save failed',
          validationErrors: errPayload?.validationErrors || null,
        });
      }
    });

    return { succeeded, failed };
  }
);
